import { Box } from '@mantine/core';
import dayjs from 'dayjs';
import { ReactElement, useMemo } from 'react';
import { ORBIT_GRAY } from '@/theme/colors';
import { Amount, Head, SummaryTable, Who } from '../ledger/SummaryCells';
import { buildByMonth, PayableFiltersState, quarterLabel, quarterMonths } from './payableRows';
import { PayablesState } from './usePayables';
import { LoadFailed } from '@/components/Hub/LoadFailed';

/** By month (BAR-3046): payee by month for the quarter, to write the payments from. */
export function PayablesByMonth({
  payables,
  filters,
  now,
}: {
  payables: PayablesState;
  filters: Pick<PayableFiltersState, 'type' | 'payee'>;
  now: Date;
}): ReactElement {
  const months = useMemo(() => quarterMonths(now), [now]);
  const groups = useMemo(() => buildByMonth(payables.payables, filters, months), [payables.payables, filters, months]);

  return (
    <Box px={16} py={12} style={{ overflow: 'auto' }}>
      <SummaryTable label="By month">
        <thead>
          <tr>
            <Head who>Payee</Head>
            {months.map((month) => (
              <Head key={month}>{dayjs(`${month}-01`).format('MMM YYYY')}</Head>
            ))}
            <Head>{quarterLabel(now)}</Head>
            <Head>Open</Head>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => [
            <tr key={group.label}>
              <th
                colSpan={months.length + 3}
                style={{ textAlign: 'left', color: ORBIT_GRAY, fontWeight: 600, padding: '10px 8px 4px' }}
              >
                {group.label}
              </th>
            </tr>,
            ...group.rows.map((row) => (
              <tr key={`${group.label}-${row.name}`}>
                <Who total={row.total}>{row.name}</Who>
                {row.months.map((value, index) => (
                  <Amount key={months[index]} value={value} total={row.total} />
                ))}
                <Amount value={row.quarter} total={row.total} bold />
                <Amount value={row.open} total={row.total} />
              </tr>
            )),
          ])}
        </tbody>
      </SummaryTable>
      {payables.error && groups.length === 0 && (
        <LoadFailed what="payables" error={payables.error} onRetry={payables.reload} />
      )}
      {!payables.loading && !payables.error && groups.length === 0 && (
        <Box py={24} style={{ textAlign: 'center', color: ORBIT_GRAY, fontSize: 13 }}>
          No lines
        </Box>
      )}
    </Box>
  );
}
